import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ServicoBio, BioPagina } from '../services/servico-bio.service';
import { AutorizacaoService } from '../../services/autorizacao.service';
import { NotificationService } from '@progress/kendo-angular-notification';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-bio-configuracoes',
  templateUrl: './bio-configuracoes.component.html',
  styleUrls: ['./bio-configuracoes.component.scss']
})
export class BioConfiguracoesComponent implements OnInit {

  bioPagina: BioPagina;
  carregando = true;
  salvando = false;
  usuario: any;
  slugDisponivel: boolean = null;
  verificandoSlug = false;
  sugestoesSlug: string[] = [];
  slugOriginal = '';
  modoPreview: 'mobile' | 'desktop' = 'mobile';
  urlPreview: SafeResourceUrl;
  timestampPreview = Date.now();

  private slugChanged = new Subject<string>();

  constructor(
    private servicoBio: ServicoBio,
    private autorizacaoService: AutorizacaoService,
    private notificationService: NotificationService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.slugChanged.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(slug => {
      this.verificarDisponibilidadeDoSlug();
    });

    this.autorizacaoService.fetchUser().subscribe(usuario => {
      this.usuario = usuario;

      if (!this.usuario?.empresa?.id) {
        this.carregando = false;
        this.mostrarNotificacao('Não foi possível carregar os dados da empresa.', 'error');
        return;
      }

      this.carregarBioPagina();
    });
  }

  carregarBioPagina() {
    this.carregando = true;
    this.servicoBio.obterBioPagina(this.usuario.empresa.id).then((data: BioPagina) => {
      if (data) {
        this.bioPagina = data;
        if (!data.slug) {
          this.bioPagina.slug = this.formatarSlug(this.usuario.empresa.dominio || '');
        }
        if (!data.urlLogo) {
          this.bioPagina.urlLogo = this.usuario.empresa.logo || '';
        }
        this.slugOriginal = data.slug || '';
      } else {
        this.bioPagina = {
          id: null,
          idEmpresa: this.usuario.empresa.id,
          slug: this.formatarSlug(this.usuario.empresa.dominio || ''),
          titulo: this.usuario.empresa.nome || '',
          subtitulo: '',
          urlLogo: this.usuario.empresa.logo || '',
          corPrimaria: '#E53935',
          corSecundaria: '#333333',
          corFundo: '#FFF8F0',
          corTexto: '#333333',
          corFundoCard: '#FFFFFF',
          corBotaoCta: null,
          borderRadius: 12,
          fonte: '',
          textoBadgePromocional: '',
          exibirOfertas: true,
          modoExibicaoOfertas: 'slide',
          ativo: false
        };
      }
      this.carregando = false;
      this.atualizarUrlPreview();
    }).catch(err => {
      this.bioPagina = {
        id: null,
        idEmpresa: this.usuario.empresa.id,
        slug: this.formatarSlug(this.usuario.empresa.dominio || ''),
        titulo: this.usuario.empresa.nome || '',
        subtitulo: '',
        urlLogo: this.usuario.empresa.logo || '',
        corPrimaria: '#E53935',
        corSecundaria: '#333333',
        corFundo: '#FFF8F0',
        corTexto: '#333333',
        corFundoCard: '#FFFFFF',
        corBotaoCta: null,
        borderRadius: 12,
        fonte: '',
        textoBadgePromocional: '',
        exibirOfertas: true,
        modoExibicaoOfertas: 'slide',
        ativo: false
      };
      this.carregando = false;
      this.atualizarUrlPreview();
    });
  }

  atualizarUrlPreview() {
    if (!this.bioPagina?.slug) {
      this.urlPreview = null;
      return;
    }
    const url = window.location.origin + '/bio/' + this.bioPagina.slug + '?preview=true&t=' + this.timestampPreview;
    this.urlPreview = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  recarregarPreview() {
    this.timestampPreview = Date.now();
    this.atualizarUrlPreview();
  }

  onSlugInput(valor: string) {
    this.bioPagina.slug = this.formatarSlug(valor);
    this.slugChanged.next(this.bioPagina.slug);
  }

  formatarSlug(valor: string): string {
    if (!valor) return '';
    return valor
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  get urlCompleta(): string {
    const dominio = window.location.origin;
    return dominio + '/bio/' + (this.bioPagina?.slug || 'seu-slug');
  }

  verificarDisponibilidadeDoSlug() {
    if (!this.bioPagina || !this.bioPagina.slug || this.bioPagina.slug.length < 3) {
      this.slugDisponivel = null;
      return;
    }
    if (this.bioPagina.slug === this.slugOriginal && this.slugOriginal) {
      this.slugDisponivel = true;
      return;
    }
    this.verificandoSlug = true;
    this.servicoBio.verificarSlug(this.bioPagina.id || 0, this.bioPagina.slug).then((data: any) => {
      this.slugDisponivel = data.disponivel;
      this.sugestoesSlug = data.sugestoes || [];
      this.verificandoSlug = false;
    }).catch(err => {
      this.slugDisponivel = null;
      this.verificandoSlug = false;
    });
  }

  usarSugestao(slug: string) {
    this.bioPagina.slug = slug;
    this.verificarDisponibilidadeDoSlug();
  }

  isGibberish(text: string): boolean {
    const limpo = (text || '').replace(/\s/g, '');
    const repetido = /^(.)\1{3,}$/i;
    const prefixos = /^(abc|123|test|xxx|qwer|asdf|zxcv)/i;
    const somenteConsoantes = /^[bcdfghjklmnpqrstvwxyz]{4,}$/i;
    return repetido.test(limpo) || prefixos.test(limpo) || somenteConsoantes.test(limpo);
  }

  salvar() {
    if (this.slugDisponivel === false) {
      this.mostrarNotificacao('O slug escolhido já está em uso. Escolha outro.', 'error');
      return;
    }

    if (!this.bioPagina.titulo || !this.bioPagina.titulo.trim()) {
      this.mostrarNotificacao('Informe o nome do restaurante.', 'error');
      return;
    }

    if (this.isGibberish(this.bioPagina.titulo)) {
      this.mostrarNotificacao('O nome do restaurante parece inválido.', 'error');
      return;
    }

    if (this.bioPagina.subtitulo && this.bioPagina.subtitulo.trim() && this.isGibberish(this.bioPagina.subtitulo)) {
      this.mostrarNotificacao('O subtítulo parece inválido.', 'error');
      return;
    }

    if (this.bioPagina.textoBadgePromocional && this.bioPagina.textoBadgePromocional.trim() && this.isGibberish(this.bioPagina.textoBadgePromocional)) {
      this.mostrarNotificacao('O texto do badge parece inválido.', 'error');
      return;
    }

    this.salvando = true;
    const operacao = this.bioPagina && this.bioPagina.id
      ? this.servicoBio.atualizarBioPagina(this.bioPagina.id, this.bioPagina)
      : this.servicoBio.salvarBioPagina({ ...this.bioPagina, idEmpresa: this.usuario.empresa.id } as BioPagina);

    operacao.then((data: BioPagina) => {
      this.bioPagina = data;
      this.slugOriginal = data.slug;
      this.salvando = false;
      this.recarregarPreview();
      this.mostrarNotificacao('Configurações salvas com sucesso!', 'success');
    }).catch(err => {
      this.salvando = false;
      this.mostrarNotificacao('Erro ao salvar. Tente novamente.', 'error');
    });
  }

  toggleAtivo() {
    if (!this.bioPagina || !this.bioPagina.id) return;
    this.servicoBio.alternarBioPagina(this.bioPagina.id, !this.bioPagina.ativo).then(() => {
      this.bioPagina.ativo = !this.bioPagina.ativo;
      this.mostrarNotificacao(
        this.bioPagina.ativo ? 'Página Bio ativada!' : 'Página Bio desativada.',
        'success'
      );
    });
  }

  copiarLink() {
    if (!this.bioPagina?.slug) return;
    navigator.clipboard.writeText(this.urlCompleta).then(() => {
      this.mostrarNotificacao('Link copiado para a área de transferência!', 'success');
    });
  }

  abrirEmNovaAba() {
    if (!this.bioPagina?.slug) return;
    window.open(this.urlCompleta, '_blank');
  }


  private mostrarNotificacao(mensagem: string, tipo: 'success' | 'error') {
    this.notificationService.show({
      content: mensagem,
      type: { style: tipo, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      hideAfter: 3000
    });
  }
}
